<template>
  <div
    v-if="!$freeParticulier() || form.bilans.length > 0"
    @keydown="progressbar.userKeyDown()"
    @keyup="progressbar.userKeyUp()"
    @click="calculate_progress"
  >
    <h3 class="m-5 md:m-15 2xl:m-20">Bilans</h3>

    <!-- BEGIN button to add a new bilan -->
    <add-button
      v-if="$can(infosPage.name_page + '_edit', infosPass.id_pass)"
      libelle="Ajouter un bilan"
      @add="addBilan()"
      class="mx-5 md:mx-15 2xl:mx-20"
    />
    <!-- END button to add a new bilan -->

    <div v-if="form.bilans.length > 0">
      <list-grid
        :list-items="form.bilans"
        :is-accordion-items="true"
        @displayNum="
          (index) => {
            bilanDisplayed = index;
          }
        "
        ref="listBilans"
      >
        <template #header>
          <div class="hidden md:block md:col-start-2 md:col-span-2">Date</div>
          <div class="col-start-3 col-span-4 md:col-span-3">Type</div>
          <div class="col-span-6 md:col-span-3">Période</div>
          <div class="hidden md:block col-span-2">Extrait du bilan</div>
          <div class="hidden md:block md:col-span-1" />
          <!--Bouton PDF-->
        </template>

        <template #accordionDescription="{ item, num }">
          <icon
            class="text-pink-600 col-span-2 md:col-span-1 place-self-center transform transition-transform ease-in-out duration-300"
            :class="{ 'rotate-90': bilanDisplayed == num }"
            name="chevron-right"
          />

          <div class="hidden md:block md:col-span-2 text-base font-normal">
            <!-- Date -->
            <span v-if="item.date_saisie_bilan">
              {{
                ('0' + new Date(item.date_saisie_bilan).getDate()).slice(-2)
              }}/{{
                ('0' + (new Date(item.date_saisie_bilan).getMonth() + 1)).slice(
                  -2
                )
              }}/{{ new Date(item.date_saisie_bilan).getFullYear() }}
            </span>
          </div>

          <div class="col-span-4 md:col-span-3">
            <!-- Type -->
            <tag
              class="hidden md:block"
              :libelle="item.type_accompagnement"
              :theme-color="
                item.type_accompagnement == 'Professionnel'
                  ? 'indigo'
                  : item.type_accompagnement == 'Personnel'
                  ? 'pink'
                  : item.type_accompagnement == 'Socio-professionnel'
                  ? 'teal'
                  : null
              "
            />
            <div
              class="text-base md:hidden"
              :class="{
                'text-indigo-600': item.type_accompagnement == 'Professionnel',
                'text-pink-600': item.type_accompagnement == 'Personnel',
                'text-teal-600':
                  item.type_accompagnement == 'Socio-professionnel',
              }"
            >
              {{ item.type_accompagnement }}
            </div>
          </div>

          <div class="col-span-6 md:col-span-3 text-base font-normal">
            <!-- Période -->
            <span v-if="item.date_debut">
              {{ ('0' + new Date(item.date_debut).getDate()).slice(-2) }}/{{
                ('0' + (new Date(item.date_debut).getMonth() + 1)).slice(-2)
              }}/{{ new Date(item.date_debut).getFullYear() }}
            </span>
            -
            <span v-if="item.date_fin">
              {{ ('0' + new Date(item.date_fin).getDate()).slice(-2) }}/{{
                ('0' + (new Date(item.date_fin).getMonth() + 1)).slice(-2)
              }}/{{ new Date(item.date_fin).getFullYear() }}
            </span>
          </div>

          <div
            class="hidden md:block md:col-span-2 text-sm italic mr-10 truncate"
          >
            <!-- Extrait du bilan -->
            {{ item.bilan_annee_ecoulee }}
          </div>
          <div class="hidden md:block md:col-span-1">
            <circle-button
              v-if="$can(constants.PERMISSION_NEOPASS_EXPORT)"
              icon="download"
              label="Exporter le bilan en PDF"
              @buttonClick="exportToPDF(item)"
            />
          </div>
        </template>

        <template #accordionContent="{ item, num }">
          <div class="grid grid-cols-12 gap-x-4 gap-y-10">
            <input-select
              name="type_accompagnement"
              :necessary="true"
              libelle="Type d'accompagnement"
              size="3"
              :value="item.type_accompagnement"
              @input="
                (newValue) => {
                  item.type_accompagnement = newValue;
                }
              "
              :datas="[
                { value: 'Professionnel' },
                { value: 'Personnel' },
                { value: 'Socio-professionnel' },
              ]"
              class="md:col-span-6"
            />
            <input-text
              name="referent_accompagnateur"
              libelle="Référent accompagnateur"
              :necessary="true"
              :value="item.referent_accompagnateur"
              @input="
                (newValue) => {
                  item.referent_accompagnateur = newValue;
                }
              "
              class="md:col-span-6"
            />
            <date-picker
              name="date_saisie_bilan"
              :necessary-date="true"
              libelle="Date de saisie du bilan"
              class="col-span-full md:col-span-6"
              format="dd/MM/yyyy"
              initialview="year"
              :value="item.date_saisie_bilan"
              @input="
                (newValue) => {
                  item.date_saisie_bilan = newValue;
                }
              "
            />
            <date-picker
              name="date_debut"
              :necessary-date="true"
              libelle="Date de début"
              class="col-span-full md:col-span-3"
              format="dd/MM/yyyy"
              initialview="year"
              :value="item.date_debut"
              @input="
                (newValue) => {
                  if (
                    (item.date_fin == '' && item.date_fin == null) ||
                    $verifyDates({
                      debut: newValue,
                      fin: item.date_fin,
                    })
                  ) {
                    item.date_debut = newValue;
                  } else {
                    badDates();
                    item.date_debut = '';
                  }
                  sortBilans();
                }
              "
            />
            <date-picker
              name="date_fin"
              :necessary-date="true"
              libelle="Date de fin"
              class="col-span-full md:col-span-3"
              format="dd/MM/yyyy"
              initialview="year"
              direction="left"
              :value="item.date_fin"
              @input="
                (newValue) => {
                  if (
                    (item.date_debut == '' && item.date_debut == null) ||
                    $verifyDates({
                      debut: item.date_debut,
                      fin: newValue,
                    })
                  ) {
                    item.date_fin = newValue;
                  } else {
                    badDates();
                    item.date_fin = '';
                  }
                }
              "
            />

            <div class="col-span-full text-base text-indigo-600 uppercase">
              Lier le bilan à un projet
            </div>

            <add-button
              libelle="Ajouter un projet"
              class="w-max"
              @add="addProject(num)"
            />

            <div
              v-for="(projet, ip) in item.projets"
              :key="'projet' + ip"
              class="col-span-full grid grid-cols-12 gap-x-4 gap-y-10"
            >
              <div class="col-span-full md:col-span-6 flex space-x-4 items-end">
                <delete-button
                  class="w-max mb-2"
                  @del="deleteItem(form.bilans[num].projets, ip, false)"
                />
                <input-select
                  :name="'projet_name' + num + ip"
                  :necessary="true"
                  libelle="Projet"
                  size="3"
                  :value="projet"
                  @input="
                    (newValue) => {
                      chooseProject(num, ip, newValue);
                    }
                  "
                  :datas="projets"
                  :value-is-object="true"
                />
              </div>

              <input-text-area
                :name="'resultat_projet' + num + ip"
                :necessary="true"
                libelle="Résultat du projet"
                size="3"
                :value="projet.resultat_projet"
                @input="
                  (newValue) => {
                    projet.resultat_projet = newValue;
                  }
                "
                class="md:col-start-1 md:col-span-6"
                :disabled="true"
              />
            </div>

            <div class="col-span-full text-base text-indigo-600">
              BILAN ET FAITS MARQUANTS
            </div>

            <input-text-area
              name="bilan_annee_ecoulee"
              :necessary="true"
              libelle="Bilan de l'année écoulée (ou des 2 années écoulées)"
              size="3"
              :value="item.bilan_annee_ecoulee"
              @input="
                (newValue) => {
                  item.bilan_annee_ecoulee = newValue;
                }
              "
              class=""
            />

            <input-text-area
              name="faits_marquants_annee"
              :necessary="true"
              libelle="Les faits marquants de cette année (ou ces 2 années écoulées) sur lesquels vous souhaitez-revenir?"
              size="3"
              :value="item.faits_marquants_annee"
              @input="
                (newValue) => {
                  item.faits_marquants_annee = newValue;
                }
              "
              class=""
            />

            <div class="col-span-full grid grid-cols-2 gap-4">
              <div
                class="col-span-full md:col-span-1 text-base text-indigo-600"
              >
                POSITIONNEMENT DU BÉNÉFICIAIRE
              </div>
              <div
                class="hidden lg:block md:col-span-1 text-base text-indigo-600"
              >
                POSITIONNEMENT DU REFERENT
              </div>
            </div>

            <!-- BEGIN grid with Positionnement BENEFICIAIRE and REFERENT -->
            <div
              class="col-span-full grid grid-flow-col grid-rows-4 grid-cols-2 gap-x-4 gap-y-10"
            >
              <!-- BEGIN Positionnement du Bénéficiaire -->
              <input-text-area
                name="acquis_pointsforts_beneficiaire"
                :necessary="true"
                libelle="Acquis/points forts"
                size="3"
                :value="item.acquis_pointsforts_beneficiaire"
                @input="
                  (newValue) => {
                    item.acquis_pointsforts_beneficiaire = newValue;
                  }
                "
                class="lg:row-span-1 md:col-span-1"
              />
              <input-text-area
                name="difficultes_beneficiaire"
                :necessary="true"
                libelle="Difficultés rencontrées"
                size="3"
                :value="item.difficultes_beneficiaire"
                @input="
                  (newValue) => {
                    item.difficultes_beneficiaire = newValue;
                  }
                "
                class="lg:row-span-1 md:col-span-1"
              />
              <input-text-area
                name="progres_beneficiaire"
                :necessary="true"
                libelle="Axes de progrès/axes à approndir"
                size="3"
                :value="item.progres_beneficiaire"
                @input="
                  (newValue) => {
                    item.progres_beneficiaire = newValue;
                  }
                "
                class="lg:row-span-1 md:col-span-1"
              />
              <input-text-area
                name="observations_beneficiaire"
                :necessary="true"
                libelle="Observations/infos complémentaires"
                size="3"
                :value="item.observations_beneficiaire"
                @input="
                  (newValue) => {
                    item.observations_beneficiaire = newValue;
                  }
                "
                class="lg:row-span-1 md:col-span-1"
              />
              <!-- END Positionnement du Bénéficiaire -->

              <div
                class="col-span-full lg:hidden md:col-span-1 text-base text-indigo-600"
              >
                POSITIONNEMENT DU REFERENT
              </div>

              <!-- BEGIN Positionnement du Référent -->
              <input-text-area
                name="acquis_pointsforts_referent"
                :necessary="true"
                libelle="Acquis/points forts"
                size="3"
                :value="item.acquis_pointsforts_referent"
                @input="
                  (newValue) => {
                    item.acquis_pointsforts_referent = newValue;
                  }
                "
                class="lg:row-span-1 md:col-span-1"
              />
              <input-text-area
                name="difficultes_referent"
                :necessary="true"
                libelle="Difficultés rencontrées"
                size="3"
                :value="item.difficultes_referent"
                @input="
                  (newValue) => {
                    item.difficultes_referent = newValue;
                  }
                "
                class="lg:row-span-1 md:col-span-1"
              />
              <input-text-area
                name="progres_referent"
                :necessary="true"
                libelle="Axes de progrès/axes à approndir"
                size="3"
                :value="item.progres_referent"
                @input="
                  (newValue) => {
                    item.progres_referent = newValue;
                  }
                "
                class="lg:row-span-1 md:col-span-1"
              />
              <input-text-area
                name="observations_referent"
                :necessary="true"
                libelle="Observations/infos complémentaires"
                size="3"
                :value="item.observations_referent"
                @input="
                  (newValue) => {
                    item.observations_referent = newValue;
                  }
                "
                class="lg:row-span-1 md:col-span-1"
              />
              <!-- END Positionnement du Référent -->
            </div>
            <!-- END grid with Positionnement BENEFICIAIRE and REFERENT -->

            <!-- BEGIN button to delete current bilan -->
            <delete-button
              class="col-span-full md:col-span-3 w-max"
              libelle="Supprimer ce bilan"
              @del="deleteItem(form.bilans, num)"
            />
            <!-- END button to delete current bilan -->
          </div>
        </template>
      </list-grid>
      <!-- END List bilans -->
    </div>
  </div>
  <div v-else>
    <h3 class="my-15 w-full">Aucun bilan</h3>
    <div v-if="$hasAtLeastOneGuest()">
      <div class="text-gray-600 text-lg text-center">
        <p>Il n'y a aucun bilan enregistré.</p>
      </div>
    </div>

    <div v-if="!$hasAtLeastOneGuest()">
      <div class="text-gray-600 text-lg text-center">
        <p>
          Veuillez ajouter un
          <router-link :to="{ name: 'PermissionsGuests' }">
            <span class="cursor-pointer text-indigo-500 underline">invité</span>
          </router-link>
          pour accéder à cette évaluation.
        </p>
      </div>
      <div class="flex justify-center w-full">
        <illustration name="notAllowed" class="mt-15" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import InputText from '../../../components/Inputs/InputText';
import InputTextArea from '../../../components/Inputs/InputTextArea';
import InputSelect from '../../../components/Inputs/InputSelect';
import MultipleSelect from '../../../components/Inputs/MultipleSelect';
import DatePicker from '../../../components/Inputs/DatePicker';
import CheckboxGreen from '../../../components/Inputs/CheckboxGreen';
import CheckboxForm from '../../../components/Inputs/CheckboxForm';
import AddButton from './../../../components/Inputs/AddButton.vue';
import DeleteButton from './../../../components/Inputs/DeleteButton.vue';

import Tag from './../../../components/Tag.vue';
import Illustration from '../../../components/Illustration.vue';
import Icon from '../../../components/Icon';

import ListGrid from './../../../components/AccordionTable/ListGrid.vue';

import VerifyDates from '../../../mixins/VerifyDates';
import Constants from './../../../constants';
import axios from './../../../api';
import CircleButton from '../../../components/CircleButton.vue';
import JsFileDownload from 'js-file-download';

export default {
  components: {
    InputText,
    InputTextArea,
    InputSelect,
    MultipleSelect,
    DatePicker,
    CheckboxGreen,
    CheckboxForm,
    Icon,
    Illustration,
    Tag,
    AddButton,
    DeleteButton,
    ListGrid,
    CircleButton,
  },

  props: ['progressbar'],

  name: 'Bilans',

  mixins: [VerifyDates],

  data() {
    return {
      bilanDisplayed: -1,
      form: {
        bilans: [],
      },
      projets: [],
      constants: Constants,
    };
  },

  async mounted() {
    await this.initForm(); // asynchrone function to wait do actions, then re-pass isFormChanged to false (actions in form pass this var to true)
    this.calculate_progress();
    this.infosPage.isFormChanged = false; // here, change value of isFormChanged to false to indicate that there is not change in form
  },

  methods: {
    ...mapMutations({
      SET_FORMCONTENT: 'neopass/SET_FORMCONTENT',
      calculate_progress: 'neopass/calculate_progress',
      SET_INFOSPAGES: 'neopass/SET_INFOSPAGE',
    }),

    async loadProjets() {
      await axios
        .get(
          '/api/neopass_datas/' +
            this.infosPass.id_pass +
            '/' +
            this.pageProjetId
        )
        .then((response) => {
          /* Load datas for the form */
          let datas = response.data.data;

          if (datas !== false && datas !== undefined && datas !== null) {
            //to verify there are datas
            this.projets = datas.projets.reduce(
              (acc, p) => [
                ...acc,
                {
                  value: p.intitule_projet,
                  commentaire_projet: p.comment_projet,
                  resultat_projet: p.resultat,
                  id: p.id,
                },
              ],
              []
            );
          } else {
            this.projets = [];
          }
        })
        .catch((error) => {
          if (error.response.status === 403) {
            this.projets = [];
          }
          console.log('ERRRR:: ', error);
        });
    },

    initForm() {
      //load values in form and keep formContent with initial values to compare when leave page without saving
      Object.assign(this.form, this.formContent);

      this.sortBilans(false);
      this.loadProjets();
    },

    async exportToPDF(bilan) {
      //this.typeExportInProgress = type;

      await axios({
        url: '/api/pdf/neopass/bilan/' + this.infosPass.id_pass,
        method: 'POST',
        responseType: 'blob', // Important
        data: {
          bilan: bilan,
        },
      }).then((response) => {
        let filename = `Bilan - ${this.infosPass.firstname} ${this.infosPass.lastname}.pdf`;

        JsFileDownload(response.data, filename);
        //this.typeExportInProgress = '';
      });
    },

    async addBilan() {
      await this.form.bilans.unshift({
        date_saisie_bilan: '',
        type_accompagnement: '',
        date_debut: '',
        date_fin: '',
        bilan_annee_ecoulee: '',
        faits_marquants_annee: '',
        acquis_pointsforts_beneficiaire: '',
        difficultes_beneficiaire: '',
        progres_beneficiaire: '',
        observations_beneficiaire: '',
        acquis_pointsforts_referent: '',
        difficultes_referent: '',
        progres_referent: '',
        observations_referent: '',
        projets: [],
      });

      await this.$refs.listBilans.displayNum(0, true);
    },

    deleteItem(el, index, closeAccordion = true) {
      el.splice(index, 1);
      if (closeAccordion) this.$refs.listBilans.displayNum(-1);
    },

    addProject(numBilan) {
      this.form.bilans[numBilan].projets.unshift({
        value: '',
        resultat_projet: '',
        commentaire_projet: '',
        id: -1,
      });
    },

    chooseProject(numBilan, numProjet, projet) {
      this.form.bilans[numBilan].projets[numProjet].value = projet.value;
      this.form.bilans[numBilan].projets[numProjet].commentaire_projet =
        projet.commentaire_projet;
      this.form.bilans[numBilan].projets[numProjet].resultat_projet =
        projet.resultat_projet;
      this.form.bilans[numBilan].projets[numProjet].id = projet.id;
    },

    sortBilans(sorting = true) {
      let bilansDisplayed = this.form.bilans[this.bilanDisplayed];

      this.form.bilans.sort(function (b1, b2) {
        let date1 = new Date(b1.date_debut);
        let date2 = new Date(b2.date_debut);

        if (date1 < date2) {
          return 1;
        } else if (date1 > date2) {
          return -1;
        } else {
          return 0;
        }
      });
      if (sorting) {
        this.$refs.listBilans.displayNum(
          this.form.bilans.indexOf(bilansDisplayed),
          true
        );
      }
    },

    badDates() {
      window.alert('La date de début doit être antérieure à la date de fin.');
    },
  },

  computed: {
    ...mapGetters({
      formContent: 'neopass/formContent',
      infosPage: 'neopass/infosPage',
      infosPass: 'neopass/infosPass',
      listModulesPages: 'neopass/listModulesPages',
    }),

    pageProjetId() {
      return this.listModulesPages
        .filter(
          (mp) => mp.name === Constants.MODULE_NEOPASS_FORM_PLAN_ACCOMPAGNEMENT
        )[0]
        .pages.filter((p) => p.name === Constants.PAGE_NEOPASS_FORM_PROJETS)[0]
        .id;
    },
  },

  watch: {
    form: {
      handler() {
        //assign true to flag to know if there is a change in form
        this.infosPage.isFormChanged = true;
      },
      deep: true,
    },
  },

  computed: {
    ...mapGetters({
      formContent: 'neopass/formContent',
      infosPage: 'neopass/infosPage',
      infosPass: 'neopass/infosPass',
      listModulesPages: 'neopass/listModulesPages',
    }),

    pageProjetId() {
      return this.listModulesPages
        .filter(
          (mp) => mp.name === Constants.MODULE_NEOPASS_FORM_PLAN_ACCOMPAGNEMENT
        )[0]
        .pages.filter((p) => p.name === Constants.PAGE_NEOPASS_FORM_PROJETS)[0]
        .id;
    },
  },

  watch: {
    form: {
      handler() {
        //assign true to flag to know if there is a change in form
        this.infosPage.isFormChanged = true;
      },
      deep: true,
    },
  },

  directives: {
    clickOutside: {
      bind: function (el, binding, vNode) {
        // Provided expression must evaluate to a function.
        if (typeof binding.value !== 'function') {
          const compName = vNode.context.name;
          let warn = `[Vue-click-outside:] provided expression '${binding.expression}' is not a function, but has to be`;
          if (compName) {
            warn += `Found in component '${compName}'`;
          }

          console.warn(warn);
        }
        // Define Handler and cache it on the element
        const bubble = binding.modifiers.bubble;
        const handler = (e) => {
          if (bubble || (!el.contains(e.target) && el !== e.target)) {
            binding.value(e);
          }
        };
        el.__vueClickOutside__ = handler;

        // add Event Listeners
        document.addEventListener('click', handler);
      },

      unbind: function (el, binding) {
        // Remove Event Listeners
        document.removeEventListener('click', el.__vueClickOutside__);
        el.__vueClickOutside__ = null;
      },
    },
  },
};
</script>
